import { UiButton, UiHStack, UiInput } from '@/lib/ui';
import { 
  FaCcVisa, 
  FaCcMastercard, 
  FaCcAmex, 
  FaCcDiscover, 
  FaCcJcb,
  FaCcDinersClub,
  FaCreditCard 
} from 'react-icons/fa';
import { FormControl, FormErrorMessage, FormLabel, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { Field, type FieldProps, Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import validator from 'card-validator';

interface CardNabFormValues {
  cardNumber: string
  expiry: string
  cvv: string
}

interface CardNabFormProps {
  handleSubmit: (values: CardNabFormValues) => void
}

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const formatCardNumber: (value: string) => string = (value) => {
  const cleanedValue = value.replace(/\D/g, '');
  const formattedValue = cleanedValue.replace(/(\d{4})/g, '$1 ').trim();
  return formattedValue;
};

const clearNumber = (value: string) => {
  return value.replace(/\D+/g, '');
};

const formatExpirationDate = (value: string) => {
  const clearValue = clearNumber(value);

  if (clearValue.length >= 3) {
    return `${clearValue.slice(0, 2)}/${clearValue.slice(2, 4)}`;
  }

  return clearValue;
};

const expiryDateValidation = Yup.string()
  .matches(/^(0[1-9]|1[0-2])\/?([0-9]{2})$/, 'Expiry date must be in MM/YY format')
  .test('expiryDate', 'Expiry date must be in the future', function (value) {
    if (!value) return false;
    const [month, year] = value.split('/').map(Number);
    const currYear = currentYear % 100;
    if (year > currYear || (year === currYear && month >= currentMonth)) {
      return true;
    }
    return false;
  });

const validationSchema = Yup.object().shape({
  cardNumber: Yup.string().min(16, 'Invalid card number').required('Card number is required'),
  expiry: expiryDateValidation.required('Expiry date is required'),
  cvv: Yup.string().required('CVV is required').min(3, 'CVV should be no less than 3 digits').max(4, 'CVV should be no more than 4 digits'),
});

const getCardIcon = (cardType: string | undefined) => {
  const iconProps = { size: 24, color: '#718096' };
  
  switch (cardType?.toLowerCase()) {
    case 'visa':
      return <FaCcVisa {...iconProps} />;
    case 'mastercard':
      return <FaCcMastercard {...iconProps} />;
    case 'american express':
      return <FaCcAmex {...iconProps} />;
    case 'discover':
      return <FaCcDiscover {...iconProps} />;
    case 'jcb':
      return <FaCcJcb {...iconProps} />;
    case 'diners club':
      return <FaCcDinersClub {...iconProps} />;
    default:
      return <FaCreditCard {...iconProps} />;
  }
};

const CardNabForm = ({ handleSubmit }: CardNabFormProps) => {
  const [cardType, setCardType] = useState<string | undefined>(undefined);
  return (
    <Formik
      initialValues={{
        cardNumber: '',
        expiry: '',
        cvv: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => {
        return (
          <Form>
            <Field name="cardNumber">
              {({ field, form }: FieldProps) => {
                return (
                  <FormControl isInvalid={!!errors.cardNumber && !!touched.cardNumber} isRequired>
                    <FormLabel htmlFor="cardNumber">Card Number</FormLabel>
                    <InputGroup>
                      <InputLeftElement display="flex" alignItems="center" pl={2} >
                        {getCardIcon(cardType)}
                      </InputLeftElement>
                      <UiInput
                        {...field}
                        pl={50}
                        type="text"
                        placeholder="1234 5678 9012 3456"
                        focusBorderColor="blue.500"
                        maxLength={19}
                        onChange={async (e) => {
                          const formattedValue = formatCardNumber(e.target.value);
                          const card = validator.number(formattedValue).card;
                          setCardType(card?.niceType);
                          await form.setFieldValue(field.name, formattedValue);
                        }} />
                    </InputGroup>
                  </FormControl>
                );
              }}
            </Field>
            <UiHStack alignItems='flex-start' paddingTop={4}>
              <Field name="expiry">
                {
                  ({ field, form }: FieldProps) => {
                    return (
                      <FormControl isInvalid={!!errors.expiry && !!touched.expiry} isRequired>
                        <FormLabel htmlFor="expiryMonth">Expiry Date</FormLabel>
                        <UiInput
                          {...field}
                          type="text"
                          pattern='\d\d/\d\d'
                          placeholder="MM/YY"
                          focusBorderColor="blue.500"
                          maxLength={5}
                          onChange={async (e) => {
                            const formattedValue = formatExpirationDate(e.target.value);
                            await form.setFieldValue(field.name, formattedValue);
                          }} />
                        {!!errors.expiry && !!touched.expiry && <FormErrorMessage>{errors.expiry}</FormErrorMessage>}
                      </FormControl>
                    );
                  }
                }
              </Field>
              <Field name="cvv">
                {
                  ({ field, form }: FieldProps) => {
                    return (
                      <FormControl isInvalid={!!errors.cvv && !!touched.cvv} isRequired>
                        <FormLabel htmlFor="cvv">CVV</FormLabel>
                        <UiInput
                          {...field}
                          type="text"
                          pattern="[0-9]*"
                          placeholder="123"
                          focusBorderColor="blue.500"
                          max={999} />
                        {!!errors.cvv && !!touched.cvv && <FormErrorMessage>{errors.cvv}</FormErrorMessage>}
                      </FormControl>
                    );
                  }
                }
              </Field>
            </UiHStack>
            <UiHStack h={8} />
            <UiHStack justifyContent={'flex-end'}>
              <UiButton type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
                Pay now
              </UiButton>
            </UiHStack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CardNabForm;
